import React, { useCallback, useEffect, useState } from 'react';
import { Calculator } from 'lucide-react';
import toolkitService from '../../services/ToolkitService';
import Button from '../shared/Button';
import useOnPrem, { rowsOf } from './useOnPrem';

// The character UDF a profile re-send stamps (toolkit onprem.js WRITES.resync-profiles), shared by every screen that
// re-sends v5 profiles through OXI — Profile Re-sync, and AR Accounts for profiles not in Opera Cloud yet.
//
// "Find a free UDF" asks the property which UDFs any profile uses (catalogue key udfs) and picks the first unused
// UDFC01–40. The choice is remembered per property (toolkit storage config profile-resync-udf), so every screen and
// every colleague stamps the same field.

export const UDF_FIELDS = Array.from({ length: 40 }, (_, i) => `UDFC${String(i + 1).padStart(2, '0')}`);
const CONFIG_KEY = 'profile-resync-udf';

export const useResyncUdf = (ctx, ready) => {
    const { ask } = useOnPrem(ctx);
    const [udf, setUdf] = useState('');
    const [message, setMessage] = useState('');

    useEffect(() => {
        if (!ready) return;
        toolkitService.storage(ctx, 'get-config', { configKey: CONFIG_KEY })
            .then(({ data }) => { if (data.result?.value) { setUdf(data.result.value); setMessage('Remembered for this property.'); } })
            .catch(() => {});
    }, [ready, ctx]);

    const findFree = useCallback(async () => {
        setMessage('Asking SyncConnect which UDFs are in use…');
        try {
            const d = await ask('udfs');
            if (d.status !== 'RETURNED') { setMessage(d.error_message || `No answer yet (${d.status}).`); return; }
            const counts = rowsOf(d.result)[0] || {};
            const free = UDF_FIELDS.find((c) => Number(counts[c.toLowerCase()] || 0) === 0);
            if (!free) { setMessage('Every character UDF (UDFC01–UDFC40) is in use — choose one deliberately.'); return; }
            setUdf(free);
            await toolkitService.storage(ctx, 'save-config', { configKey: CONFIG_KEY, value: free });
            setMessage(`${free} is unused by every profile at the property — remembered for next time.`);
        } catch (e) {
            setMessage(e?.response?.data?.message || e.message);
        }
    }, [ask, ctx]);

    return { udf, setUdf, message, findFree };
};

function ToolkitResyncUdf({ resync, disabled }) {
    const { udf, setUdf, message, findFree } = resync;
    return (
        <>
            <div className="tkb-row">
                <label>
                    <span>Character UDF</span>
                    <select value={udf} onChange={(e) => setUdf(e.target.value)}>
                        <option value="">Choose…</option>
                        {UDF_FIELDS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                </label>
                <Button variant="secondary" onClick={findFree} disabled={disabled}><Calculator className="icon" /> Find a free UDF</Button>
            </div>
            {message && <p className="tkb-msg">{message}</p>}
        </>
    );
}

export default ToolkitResyncUdf;
